import { ActionIcon, Input, TextInput, Tooltip } from '@mantine/core';
import { useField } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import IconSearch from '@tabler/icons-react/dist/esm/icons/IconSearch.mjs';
import IconSelectAll from '@tabler/icons-react/dist/esm/icons/IconSelectAll.mjs';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGraphRendererApi } from '../model/use-api';
import { GraphControlsPortal } from './controls';

export const Search = () => {
  const hasMatchedRef = useRef(false);
  const { t } = useTranslation();
  const getAPI = useGraphRendererApi();
  const handleSearch = (value: string) => {
    const api = getAPI();
    if (!api) return;
    api.setVisibilityFilter({ search: value });

    if (!value.trim()) {
      if (!hasMatchedRef.current) api.fit({ padding: 40, duration: 300 });
      hasMatchedRef.current = false;
      return;
    }

    const nodes = api.getActiveNodes();
    if (nodes.length) {
      hasMatchedRef.current = true;
      if (nodes.length === 1) {
        api.focusNode(nodes[0].data.id, { zoom: 2, duration: 300 });
      } else {
        api.fit({
          nodeIds: nodes.map(({ data }) => data.id),
          padding: 80,
          duration: 300,
        });
      }
    }
  };

  const handleSelectActiveCIs = () => {
    const api = getAPI();
    if (!api) return;
    const ids = api
      .getActiveNodes()
      .filter(({ data }) => !!data.ci_name)
      .map(({ data }) => data.id);
    api.clearSelection();
    api.setSelectedNodes(ids);
  };

  const debouncedSearch = useDebouncedCallback(handleSearch, 200);
  const field = useField({
    initialValue: '',
    onValueChange: debouncedSearch,
  });
  return (
    <GraphControlsPortal>
      <TextInput
        data-testid='graph-menu-text-filter'
        {...field.getInputProps()}
        size='xs'
        placeholder={t('placeholders.search')}
        leftSection={<IconSearch size={16} />}
        w={200}
        rightSection={
          field.isDirty() && (
            <Input.ClearButton
              data-testid='graph-menu-text-filter-clear'
              size='xs'
              onClick={field.reset}
            />
          )
        }
      />
      <Tooltip label={t('graphView.panel.selectActiveCis', { ns: 'srm' })}>
        <ActionIcon
          aria-label={t('graphView.panel.selectActiveCis', { ns: 'srm' })}
          data-testid='graph-menu-select-active-cis'
          variant='light'
          color='blue'
          size='md'
          onClick={handleSelectActiveCIs}
        >
          <IconSelectAll size={18} />
        </ActionIcon>
      </Tooltip>
    </GraphControlsPortal>
  );
};
